import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import avatar from 'Assets/layout/prosJobList/avatar-yellow.svg';
import AvatarStack, { AvatarStackPro } from 'Douleutaras/components/AvatarStack/AvatarStack';

import './PersonalDetailsProsBanner.scss';

// Design shows at most three bid-objective avatars before the availability counter.
const VISIBLE_AVATAR_CAP = 3;
// Availability collapses to "+99" once we know there are at least this many pros.
const AVAILABILITY_DISPLAY_CAP = 100;
const MAX_COUNTER = 99;

export interface PersonalDetailsProsBannerProps {
  // Bid objective for the subcategory/location (posting_flow `max_bids`).
  bidObjective: number;
  // Total available professionals (posting_flow `pros_available_info.pros_available`).
  // Undefined when availability info is unknown — the +X counter is then hidden.
  availableProfessionals?: number;
}

const PersonalDetailsProsBanner = ({ bidObjective, availableProfessionals }: PersonalDetailsProsBannerProps) => {
  const { t } = useTranslation();

  const avatarCount = Math.min(bidObjective, VISIBLE_AVATAR_CAP);
  const hasAvailability = availableProfessionals !== undefined;
  const extraAvailable = hasAvailability
    ? (availableProfessionals >= AVAILABILITY_DISPLAY_CAP ? MAX_COUNTER : availableProfessionals - bidObjective)
    : 0;
  // Drives both the +X counter avatar and the extended subtitle copy.
  const hasExtraAvailable = extraAvailable > 0;

  // No pro-mugshots are available at this step, so the stack is filled with a
  // generic placeholder avatar. `isTopPro` gives each item the yellow ring from
  // the design (it only tints the ring — it does not add a top-pro badge).
  const avatarStackPros: Array<AvatarStackPro> = Array.from({ length: avatarCount })
    .map((unused, index) => ({
      id: `pros-banner-avatar-${index}`,
      mugshot: avatar,
      name: '',
      isTopPro: false
    }));

  return (
    <>
    <div styleName="pros-banner">
      <div styleName="pros-banner__content">
        <p styleName="pros-banner__title">
          {t('T.POST-JOB.PROS-BANNER.TITLE', 'Good news!')}
        </p>
        <p styleName="pros-banner__subtitle">
          {hasExtraAvailable
            ? <Trans
                i18nKey="T.POST-JOB.PROS-BANNER.SUBTITLE-WITH-EXTRA"
                defaults="<bold>{{count}}</bold> professionals are ready for your job and <bold>{{extra}}</bold> more are available."
                values={{ count: bidObjective, extra: extraAvailable }}
                components={{ bold: <span styleName="pros-banner__count-text" /> }}
              />
            : <Trans
                i18nKey="T.POST-JOB.PROS-BANNER.SUBTITLE"
                defaults="<bold>{{count}}</bold> professionals are ready for your job."
                values={{ count: bidObjective }}
                components={{ bold: <span styleName="pros-banner__count-text" /> }}
              />
          }
        </p>
      </div>
      <AvatarStack pros={avatarStackPros} totalCount={extraAvailable} size="small" />
    </div>
    <p styleName="pros-banner-prompt">
      {t('T.POST-JOB.PROS-BANNER.DETAILS-PROMPT', 'Fill in your details to get quotes right away.')}
    </p>
    </>
  );
};

export default PersonalDetailsProsBanner;
